import { useEffect, useState, useRef } from "react";
import { Save, Upload, Eye, Trash2, ArrowUp, ArrowDown, Plus } from "lucide-react";
import { api } from "@/lib/api";

const LANGS = ["EN", "FR", "PT"];
const cloneDeep = (o) => JSON.parse(JSON.stringify(o));

export const Field = ({ label, value, onChange, area, hint }) => (
  <div className="mb-3">
    <label className="field-label">{label}</label>
    {area ? (
      <textarea rows={3} className="field-input resize-none" value={value || ""} onChange={(e) => onChange(e.target.value)} />
    ) : (
      <input className="field-input" value={value || ""} onChange={(e) => onChange(e.target.value)} />
    )}
    {hint && <p className="mt-1 text-[0.65rem] text-[#8A8378]">{hint}</p>}
  </div>
);

export const LangText = ({ label, value = {}, onChange, area }) => (
  <div className="mb-4 border border-[#191713]/10 p-4 bg-[#F7F3EC]/60">
    <p className="field-label !mb-3">{label}</p>
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
      {LANGS.map((l) => (
        <div key={l}>
          <span className="text-[0.6rem] tracking-[0.2em] text-[#A6853B] font-semibold">{l}</span>
          {area ? (
            <textarea rows={3} className="field-input resize-none mt-1" value={value[l] || ""}
              onChange={(e) => onChange({ ...value, [l]: e.target.value })} />
          ) : (
            <input className="field-input mt-1" value={value[l] || ""}
              onChange={(e) => onChange({ ...value, [l]: e.target.value })} />
          )}
        </div>
      ))}
    </div>
  </div>
);

const ListControls = ({ onUp, onDown, onDelete }) => (
  <div className="flex gap-2">
    <button onClick={onUp} aria-label="Move up" className="p-1.5 border border-[#191713]/15 hover:border-[#A6853B]"><ArrowUp size={13} /></button>
    <button onClick={onDown} aria-label="Move down" className="p-1.5 border border-[#191713]/15 hover:border-[#A6853B]"><ArrowDown size={13} /></button>
    <button onClick={onDelete} aria-label="Delete" className="p-1.5 border border-red-300 text-red-600 hover:bg-red-50"><Trash2 size={13} /></button>
  </div>
);

export const ImageField = ({ label, value, onChange }) => {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    setBusy(true);
    try {
      const data = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result.split(",")[1]);
        r.onerror = rej;
        r.readAsDataURL(file);
      });
      const { data: out } = await api.post("/admin/media", { filename: file.name, content_type: file.type, data, alt: "" });
      onChange(out.url);
    } finally { setBusy(false); }
  };
  return (
    <div className="mb-3">
      <label className="field-label">{label}</label>
      <div className="flex gap-2 items-start">
        <input className="field-input flex-1" value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="Image URL or upload →" />
        <button onClick={() => fileRef.current.click()} disabled={busy}
          className="btn-outline-dark !py-2.5 !px-3 text-xs whitespace-nowrap"><Upload size={13} /> {busy ? "…" : "Upload"}</button>
        <input ref={fileRef} type="file" accept="image/*" className="hidden"
          onChange={(e) => e.target.files[0] && upload(e.target.files[0])} />
      </div>
      {value && <img src={value} alt="" className="mt-2 h-16 object-cover border border-[#191713]/10" />}
    </div>
  );
};

const SUBTABS = ["hero", "services", "projects", "pricing", "testimonials", "faq", "contact", "seo", "media", "brand"];

export default function AdminCMS() {
  const [draft, setDraft] = useState(null);
  const [tab, setTab] = useState("hero");
  const [media, setMedia] = useState([]);
  const [status, setStatus] = useState("");

  useEffect(() => {
    api.get("/content/draft").then(({ data }) => setDraft(data));
    api.get("/admin/media").then(({ data }) => setMedia(data));
  }, []);

  const up = (fn) => setDraft((d) => { const c = cloneDeep(d); fn(c); return c; });
  const move = (arr, i, dir) => { const j = i + dir; if (j < 0 || j >= arr.length) return; [arr[i], arr[j]] = [arr[j], arr[i]]; };

  const saveDraft = async (silent) => {
    const clean = cloneDeep(draft);
    (clean.projects || []).forEach((p) => {
      if (Array.isArray(p.gallery)) p.gallery = p.gallery.filter((g) => g && g.trim());
    });
    setDraft(clean);
    await api.put("/admin/content", { content: clean });
    if (!silent) { setStatus("Draft saved"); setTimeout(() => setStatus(""), 2500); }
  };
  const publish = async () => {
    await saveDraft(true);
    await api.post("/admin/content/publish");
    setStatus("Published — the live site is updated"); setTimeout(() => setStatus(""), 3000);
  };
  const preview = async () => {
    await saveDraft(true);
    window.open("/?preview=draft", "_blank");
  };

  const uploadToLibrary = async (file, alt) => {
    const data = await new Promise((res, rej) => {
      const r = new FileReader(); r.onload = () => res(r.result.split(",")[1]); r.onerror = rej; r.readAsDataURL(file);
    });
    await api.post("/admin/media", { filename: file.name, content_type: file.type, data, alt });
    const { data: list } = await api.get("/admin/media");
    setMedia(list);
  };

  if (!draft) return <p className="p-8 text-sm text-[#8A8378]">Loading editor…</p>;

  return (
    <div className="bg-white border border-[#191713]/10 border-t-0" data-testid="admin-cms">
      <div className="flex flex-wrap items-center justify-between gap-3 p-4 border-b border-[#191713]/10 bg-[#F7F3EC]/50 sticky top-0 z-10">
        <div className="flex flex-wrap gap-1">
          {SUBTABS.map((s) => (
            <button key={s} data-testid={`cms-tab-${s}`} onClick={() => setTab(s)}
              className={`px-3 py-2 text-xs capitalize transition-colors ${tab === s ? "bg-[#191713] text-[#F7F3EC]" : "text-[#191713]/60 hover:text-[#191713]"}`}>
              {s}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          {status && <span className="text-xs text-emerald-700" data-testid="cms-status">{status}</span>}
          <button onClick={() => saveDraft()} data-testid="cms-save-draft" className="btn-outline-dark !py-2 !px-3 text-xs"><Save size={13} /> Save draft</button>
          <button onClick={preview} data-testid="cms-preview" className="btn-outline-dark !py-2 !px-3 text-xs"><Eye size={13} /> Preview</button>
          <button onClick={publish} data-testid="cms-publish" className="btn-gold !py-2 !px-4 text-xs">Publish</button>
        </div>
      </div>

      <div className="p-5 lg:p-8 max-h-[70vh] overflow-y-auto">
        {tab === "hero" && (
          <div data-testid="cms-hero-editor">
            <LangText label="Headline — line 1" value={draft.hero.l1} onChange={(v) => up((c) => (c.hero.l1 = v))} />
            <LangText label="Headline — line 2 (gold italic)" value={draft.hero.l2} onChange={(v) => up((c) => (c.hero.l2 = v))} />
            <LangText label="Description" area value={draft.hero.sub} onChange={(v) => up((c) => (c.hero.sub = v))} />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <LangText label="Primary CTA label" value={draft.hero.cta1} onChange={(v) => up((c) => (c.hero.cta1 = v))} />
                <Field label="Primary CTA link" value={draft.hero.cta1_link} onChange={(v) => up((c) => (c.hero.cta1_link = v))} />
              </div>
              <div>
                <LangText label="Secondary CTA label" value={draft.hero.cta2} onChange={(v) => up((c) => (c.hero.cta2 = v))} />
                <Field label="Secondary CTA link" value={draft.hero.cta2_link} onChange={(v) => up((c) => (c.hero.cta2_link = v))} />
              </div>
            </div>
            <ImageField label="Hero image" value={draft.hero.image} onChange={(v) => up((c) => (c.hero.image = v))} />
            <Field label="Hero image alt text" value={draft.hero.image_alt} onChange={(v) => up((c) => (c.hero.image_alt = v))} />
          </div>
        )}

        {tab === "services" && (
          <div data-testid="cms-services-editor">
            {draft.services.map((s, i) => (
              <div key={s.id} className="mb-6 border border-[#191713]/10 p-4">
                <div className="flex justify-between mb-3">
                  <span className="text-xs font-semibold text-[#A6853B]">Service {i + 1}</span>
                  <ListControls onUp={() => up((c) => move(c.services, i, -1))} onDown={() => up((c) => move(c.services, i, 1))}
                    onDelete={() => up((c) => c.services.splice(i, 1))} />
                </div>
                <LangText label="Name" value={s.name} onChange={(v) => up((c) => (c.services[i].name = v))} />
                <LangText label="Description" area value={s.body} onChange={(v) => up((c) => (c.services[i].body = v))} />
                <ImageField label="Image" value={s.image} onChange={(v) => up((c) => (c.services[i].image = v))} />
              </div>
            ))}
            <button data-testid="cms-add-service" className="btn-outline-dark !py-2 !px-4 text-xs"
              onClick={() => up((c) => c.services.push({ id: `s${Date.now()}`, image: "", name: {}, body: {} }))}>
              <Plus size={13} /> Add service
            </button>
          </div>
        )}

        {tab === "projects" && (
          <div data-testid="cms-projects-editor">
            {draft.projects.map((p, i) => (
              <div key={p.id} className="mb-6 border border-[#191713]/10 p-4" data-testid={`cms-project-${p.id}`}>
                <div className="flex flex-wrap justify-between gap-2 mb-3">
                  <div className="flex items-center gap-4">
                    <span className="text-xs font-semibold text-[#A6853B]">Project {i + 1}</span>
                    <label className="text-xs flex items-center gap-1.5">
                      <input type="checkbox" checked={!!p.featured} onChange={(e) => up((c) => (c.projects[i].featured = e.target.checked))} /> Featured
                    </label>
                    <label className="text-xs flex items-center gap-1.5">
                      <input type="checkbox" checked={!!p.demo} onChange={(e) => up((c) => (c.projects[i].demo = e.target.checked))} /> Demo (hidden from public site)
                    </label>
                  </div>
                  <ListControls onUp={() => up((c) => move(c.projects, i, -1))} onDown={() => up((c) => move(c.projects, i, 1))}
                    onDelete={() => up((c) => c.projects.splice(i, 1))} />
                </div>
                <Field label="Title" value={p.title} onChange={(v) => up((c) => (c.projects[i].title = v))} />
                <LangText label="Client / industry" value={p.industry} onChange={(v) => up((c) => (c.projects[i].industry = v))} />
                <LangText label="Description" area value={p.description} onChange={(v) => up((c) => (c.projects[i].description = v))} />
                <LangText label="Services provided" value={p.services_provided} onChange={(v) => up((c) => (c.projects[i].services_provided = v))} />
                <ImageField label="Cover image" value={p.image} onChange={(v) => up((c) => (c.projects[i].image = v))} />
                <p className="field-label !mb-2">Gallery images</p>
                {(p.gallery || []).map((g, gi) => (
                  <div key={gi} className="flex gap-2 items-start" data-testid={`cms-gallery-row-${p.id}-${gi}`}>
                    <div className="flex-1"><ImageField label={`Gallery image ${gi + 1}`} value={g} onChange={(v) => up((c) => (c.projects[i].gallery[gi] = v))} /></div>
                    <button onClick={() => up((c) => c.projects[i].gallery.splice(gi, 1))} aria-label="Remove gallery image"
                      data-testid={`cms-gallery-remove-${p.id}-${gi}`}
                      className="p-1.5 mt-6 border border-red-300 text-red-600"><Trash2 size={13} /></button>
                  </div>
                ))}
                <button className="btn-outline-dark !py-1.5 !px-3 text-xs mb-3" data-testid={`cms-add-gallery-${p.id}`}
                  onClick={() => up((c) => (c.projects[i].gallery = [...(c.projects[i].gallery || []), ""]))}>
                  <Plus size={12} /> Add gallery image
                </button>
                <Field label="Website link (optional)" value={p.link} onChange={(v) => up((c) => (c.projects[i].link = v))} />
              </div>
            ))}
            <button data-testid="cms-add-project" className="btn-outline-dark !py-2 !px-4 text-xs"
              onClick={() => up((c) => c.projects.push({ id: `p${Date.now()}`, title: "", image: "", link: "", featured: false, industry: {}, description: {}, services_provided: {} }))}>
              <Plus size={13} /> Add project
            </button>
          </div>
        )}

        {tab === "pricing" && (
          <div data-testid="cms-pricing-editor">
            <p className="text-xs text-[#8A8378] mb-4">Plans marked "Placeholder content" show only name, price and description publicly — their feature lists stay hidden from visitors until you untick the flag.</p>
            {draft.pricing_plans.map((p, i) => (
              <div key={p.id} className="mb-6 border border-[#191713]/10 p-4" data-testid={`cms-project-${p.id}`}>
                <div className="flex flex-wrap justify-between gap-2 mb-3">
                  <div className="flex items-center gap-4">
                    <span className="text-xs font-semibold text-[#A6853B]">Plan {i + 1}</span>
                    <label className="text-xs flex items-center gap-1.5">
                      <input type="checkbox" checked={!!p.enabled} onChange={(e) => up((c) => (c.pricing_plans[i].enabled = e.target.checked))} /> Enabled
                    </label>
                    <label className="text-xs flex items-center gap-1.5">
                      <input type="checkbox" checked={!!p.featured} onChange={(e) => up((c) => (c.pricing_plans[i].featured = e.target.checked))} /> Recommended
                    </label>
                    <label className="text-xs flex items-center gap-1.5">
                      <input type="checkbox" checked={!!p.placeholder} onChange={(e) => up((c) => (c.pricing_plans[i].placeholder = e.target.checked))} /> Placeholder content
                    </label>
                  </div>
                  <ListControls onUp={() => up((c) => move(c.pricing_plans, i, -1))} onDown={() => up((c) => move(c.pricing_plans, i, 1))}
                    onDelete={() => up((c) => c.pricing_plans.splice(i, 1))} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <LangText label="Name" value={p.name} onChange={(v) => up((c) => (c.pricing_plans[i].name = v))} />
                  <Field label="Price (AED/month)" value={p.price} onChange={(v) => up((c) => (c.pricing_plans[i].price = v))} />
                </div>
                <LangText label="Tag" value={p.tag} onChange={(v) => up((c) => (c.pricing_plans[i].tag = v))} />
                <LangText label="Description" area value={p.desc} onChange={(v) => up((c) => (c.pricing_plans[i].desc = v))} />
                <p className="field-label !mb-2">Features</p>
                {p.features.map((f, fi) => (
                  <div key={fi} className="flex gap-2 items-start mb-2">
                    <div className="flex-1"><LangText label={`Feature ${fi + 1}`} value={f} onChange={(v) => up((c) => (c.pricing_plans[i].features[fi] = v))} /></div>
                    <button onClick={() => up((c) => c.pricing_plans[i].features.splice(fi, 1))} aria-label="Remove feature"
                      className="p-1.5 mt-4 border border-red-300 text-red-600"><Trash2 size={13} /></button>
                  </div>
                ))}
                <button className="btn-outline-dark !py-1.5 !px-3 text-xs mb-3" onClick={() => up((c) => c.pricing_plans[i].features.push({}))}>
                  <Plus size={12} /> Add feature
                </button>
                <LangText label="CTA label" value={p.cta} onChange={(v) => up((c) => (c.pricing_plans[i].cta = v))} />
                <Field label="CTA link" value={p.cta_link} onChange={(v) => up((c) => (c.pricing_plans[i].cta_link = v))} />
              </div>
            ))}
            <button data-testid="cms-add-plan" className="btn-outline-dark !py-2 !px-4 text-xs"
              onClick={() => up((c) => c.pricing_plans.push({ id: `pl${Date.now()}`, enabled: true, featured: false, placeholder: true, price: "", name: {}, tag: {}, desc: {}, cta: {}, cta_link: "/contact", features: [] }))}>
              <Plus size={13} /> Add package
            </button>
          </div>
        )}

        {tab === "testimonials" && (
          <div data-testid="cms-testimonials-editor">
            <p className="text-xs text-[#8A8378] mb-4">Only add real, verified client testimonials. The public site shows a placeholder note while this list is empty.</p>
            {draft.testimonials.map((tst, i) => (
              <div key={tst.id} className="mb-5 border border-[#191713]/10 p-4">
                <div className="flex justify-between mb-3">
                  <span className="text-xs font-semibold text-[#A6853B]">Testimonial {i + 1}</span>
                  <ListControls onUp={() => up((c) => move(c.testimonials, i, -1))} onDown={() => up((c) => move(c.testimonials, i, 1))}
                    onDelete={() => up((c) => c.testimonials.splice(i, 1))} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Name" value={tst.name} onChange={(v) => up((c) => (c.testimonials[i].name = v))} />
                  <Field label="Business" value={tst.business} onChange={(v) => up((c) => (c.testimonials[i].business = v))} />
                </div>
                <LangText label="Quote" area value={tst.quote} onChange={(v) => up((c) => (c.testimonials[i].quote = v))} />
              </div>
            ))}
            <button data-testid="cms-add-testimonial" className="btn-outline-dark !py-2 !px-4 text-xs"
              onClick={() => up((c) => c.testimonials.push({ id: `t${Date.now()}`, name: "", business: "", quote: {} }))}>
              <Plus size={13} /> Add testimonial
            </button>
          </div>
        )}

        {tab === "faq" && (
          <div data-testid="cms-faq-editor">
            {draft.faq.map((f, i) => (
              <div key={f.id} className="mb-5 border border-[#191713]/10 p-4">
                <div className="flex justify-between mb-3">
                  <span className="text-xs font-semibold text-[#A6853B]">Question {i + 1}</span>
                  <ListControls onUp={() => up((c) => move(c.faq, i, -1))} onDown={() => up((c) => move(c.faq, i, 1))}
                    onDelete={() => up((c) => c.faq.splice(i, 1))} />
                </div>
                <LangText label="Question" value={f.q} onChange={(v) => up((c) => (c.faq[i].q = v))} />
                <LangText label="Answer" area value={f.a} onChange={(v) => up((c) => (c.faq[i].a = v))} />
              </div>
            ))}
            <button data-testid="cms-add-faq" className="btn-outline-dark !py-2 !px-4 text-xs"
              onClick={() => up((c) => c.faq.push({ id: `f${Date.now()}`, q: {}, a: {} }))}>
              <Plus size={13} /> Add question
            </button>
          </div>
        )}

        {tab === "contact" && (
          <div data-testid="cms-contact-editor" className="max-w-lg">
            <Field label="Email" value={draft.contact.email} onChange={(v) => up((c) => (c.contact.email = v))} />
            <Field label="Phone" value={draft.contact.phone} onChange={(v) => up((c) => (c.contact.phone = v))} />
            <Field label="WhatsApp number (international format, e.g. 9715xxxxxxxx)" value={draft.contact.whatsapp}
              onChange={(v) => up((c) => (c.contact.whatsapp = v))}
              hint="Leave empty to hide the floating WhatsApp button. Enter your real number — none has been invented." />
            <Field label="Address / location" value={draft.contact.address} onChange={(v) => up((c) => (c.contact.address = v))} />
            <Field label="Instagram URL" value={draft.contact.instagram} onChange={(v) => up((c) => (c.contact.instagram = v))} />
            <Field label="LinkedIn URL" value={draft.contact.linkedin} onChange={(v) => up((c) => (c.contact.linkedin = v))} />
          </div>
        )}

        {tab === "seo" && (
          <div data-testid="cms-seo-editor">
            {["home", "pricing", "contact"].map((page) => (
              <div key={page} className="mb-6 border border-[#191713]/10 p-4">
                <p className="text-xs font-semibold text-[#A6853B] uppercase mb-3">{page} page</p>
                <Field label="SEO title" value={draft.seo[page].title} onChange={(v) => up((c) => (c.seo[page].title = v))} />
                <Field label="Meta description" area value={draft.seo[page].description} onChange={(v) => up((c) => (c.seo[page].description = v))} />
                <ImageField label="Social sharing image (Open Graph)" value={draft.seo[page].og_image} onChange={(v) => up((c) => (c.seo[page].og_image = v))} />
              </div>
            ))}
          </div>
        )}

        {tab === "media" && (
          <div data-testid="cms-media-editor">
            <MediaUploader onUpload={uploadToLibrary} />
            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
              {media.map((m) => (
                <div key={m.id} className="border border-[#191713]/10 p-2">
                  <img src={m.url} alt={m.alt || m.filename} className="w-full h-24 object-cover" />
                  <p className="mt-2 text-[0.65rem] text-[#8A8378] truncate">{m.filename}</p>
                  <div className="flex items-center justify-between mt-1">
                    <button className="text-[0.65rem] text-[#A6853B]" onClick={() => navigator.clipboard.writeText(m.url)}>Copy URL</button>
                    <button className="text-red-600" aria-label="Delete media"
                      onClick={async () => { await api.delete(`/admin/media/${m.id}`); setMedia(media.filter((x) => x.id !== m.id)); }}>
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
              ))}
              {media.length === 0 && <p className="text-sm text-[#8A8378] col-span-4">No uploads yet.</p>}
            </div>
          </div>
        )}

        {tab === "brand" && (
          <div data-testid="cms-brand-editor" className="max-w-xl">
            <p className="text-xs text-[#8A8378] mb-4">Brand assets update everywhere they are used (navigation, footer, login, portals, favicon) after publishing.</p>
            <ImageField label="Primary logo (full official logo)" value={draft.brand?.logo_primary} onChange={(v) => up((c) => (c.brand.logo_primary = v))} />
            <ImageField label="Logo for dark backgrounds" value={draft.brand?.logo_dark} onChange={(v) => up((c) => (c.brand.logo_dark = v))} />
            <ImageField label="Logo for light backgrounds" value={draft.brand?.logo_light} onChange={(v) => up((c) => (c.brand.logo_light = v))} />
            <ImageField label="Favicon" value={draft.brand?.favicon} onChange={(v) => up((c) => (c.brand.favicon = v))} />
            <ImageField label="Social sharing image (Open Graph)" value={draft.brand?.og_image} onChange={(v) => up((c) => (c.brand.og_image = v))} />
            <div className="grid grid-cols-3 gap-4 mt-2">
              {[["color_primary", "Primary color"], ["color_secondary", "Secondary color"], ["color_accent", "Accent color"]].map(([k, label]) => (
                <div key={k}>
                  <label className="field-label">{label}</label>
                  <div className="flex gap-2 items-center">
                    <input type="color" value={draft.brand?.[k] || "#000000"} onChange={(e) => up((c) => (c.brand[k] = e.target.value))}
                      className="w-9 h-9 border border-[#191713]/15 p-0.5" aria-label={label} />
                    <input className="field-input !py-1.5 text-xs" value={draft.brand?.[k] || ""} onChange={(e) => up((c) => (c.brand[k] = e.target.value))} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const MediaUploader = ({ onUpload }) => {
  const ref = useRef(null);
  const [alt, setAlt] = useState("");
  const [busy, setBusy] = useState(false);
  return (
    <div className="flex flex-wrap gap-3 items-end">
      <div className="flex-1 min-w-[200px]">
        <Field label="Alt text for next upload" value={alt} onChange={setAlt} />
      </div>
      <button data-testid="cms-media-upload" onClick={() => ref.current.click()} disabled={busy} className="btn-gold !py-2.5 !px-4 text-xs mb-3">
        <Upload size={13} /> {busy ? "Uploading…" : "Upload image"}
      </button>
      <input ref={ref} type="file" accept="image/*" className="hidden"
        onChange={async (e) => { if (e.target.files[0]) { setBusy(true); await onUpload(e.target.files[0], alt); setBusy(false); setAlt(""); } }} />
    </div>
  );
};
